/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import {
  CCard, CCardBody, CCardHeader, CRow, CCol,
  CFormInput, CFormLabel, CFormSelect, CButton, CSpinner, CAlert,
   CProgress
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import {
  cilArrowLeft, cilSave, cilX, cilInfo, cilFolderOpen, cilTruck,
  cilCheckCircle, cilXCircle, cilWarning, cilArrowRight, cilArrowLeft as cilArrowLeftIcon
} from '@coreui/icons';
import { orderActionRequest } from '../../store/order/orderActions';
import { getOrderActionDropdown } from '../../services/endpoints';
import './OrderAction.scss';
import '../../components/OrderInputs/OrderInputs.scss';
import { toScaledIntegerStringFromParts, formatScaledAmount } from '../../helpers/quantity';

function OrderAction(props) {
  const { orderActionRequest, isLoading } = props;
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedActions, setSelectedActions] = useState({
    orderStatus: false,
    stock: false,
    shipment: false
  });
  const [orderInfo, setOrderInfo] = useState(null);

  const [formData, setFormData] = useState({
    // Order Action
    NewOrderStatusGuid: '',
    
    // Stock Action
    StockPrdUnitGuid: '',
    OpsStockStatusGuid: '',
    ProductStockQuantity: '',
    StockRollCount: '',
    StockPlace: '',
    Lot: '',
    StockDescription: '',
    
    // Shipment Action
    StockQuantity: '',
    OpsShipmentStatusGuid: '',
    ShipmentPrdUnitGuid: '',
    BillingCompany: '',
    ShipmentDatetime: '',
    DeliveryNo: '',
    ShipmentDescription: '',
    ProductShippedQuantity: '',
    ShipmentRollCount: '',
    ShipmentLot: '',
    NetWeight: '',
    GrossWeight: ''
  });

  // Decimal-split inputs for quantities (always two decimals)
  const [stockQuantityInputs, setStockQuantityInputs] = useState({ stockWhole: '', stockDecimal: '' });
  const [shippedQuantityInputs, setShippedQuantityInputs] = useState({ shippedWhole: '', shippedDecimal: '' });
  const [shipmentNetWeightInputs, setShipmentNetWeightInputs] = useState({ netWhole: '', netDecimal: '' });
  const [shipmentGrossWeightInputs, setShipmentGrossWeightInputs] = useState({ grossWhole: '', grossDecimal: '' });

  const [errors, setErrors] = useState({});
  const [dropdownLoading, setDropdownLoading] = useState(true);
  const [dropdownError, setDropdownError] = useState(null);
  const [orderStatusOptions, setOrderStatusOptions] = useState([]);
  const [stockStatusOptions, setStockStatusOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [shipmentStatusOptions, setShipmentStatusOptions] = useState([]);

  useEffect(() => {
    if (location.state?.order) {
      setOrderInfo(location.state.order);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      setDropdownLoading(true);
      setDropdownError(null);
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const sessionUserId = localStorage.getItem('sessionUserId');
        const userId = sessionUserId || userData?.sessionUserId;
        const payload = { SessionUserId: userId };
        const response = await getOrderActionDropdown(payload);
        const data = response?.data || {};
        setOrderStatusOptions(data.OrderStatuses || []);
        setStockStatusOptions(data.StockStatuses || []);
        setUnitOptions(data.Units || []);
        setShipmentStatusOptions(data.ShipmentStatuses || []);
      } catch (err) {
        setDropdownError('Dropdown verileri yüklenemedi.');
        setOrderStatusOptions([]);
        setStockStatusOptions([]);
        setUnitOptions([]);
        setShipmentStatusOptions([]);
      } finally {
        setDropdownLoading(false);
      }
    };
    fetchDropdownData();
  }, []);

  const steps = [
    {
      id: 'selection',
      title: t('actionSelection') || 'İşlem Seçimi',
      description: t('selectActionType') || 'Hangi işlemleri gerçekleştirmek istediğinizi seçin'
    },
    {
      id: 'orderStatus',
      title: t('orderStatusChange') || 'Durum Değişikliği',
      description: t('configureOrderStatus') || 'Sipariş durumunu yapılandırın',
      visible: selectedActions.orderStatus
    },
    {
      id: 'stock',
      title: t('stockAction') || 'Stok İşlemi',
      description: t('configureStockAction') || 'Stok işlemini yapılandırın',
      visible: selectedActions.stock
    },
    {
      id: 'shipment',
      title: t('shipmentAction') || 'Sevkiyat İşlemi',
      description: t('configureShipmentAction') || 'Sevkiyat işlemini yapılandırın',
      visible: selectedActions.shipment
    },
    {
      id: 'review',
      title: t('reviewAndSubmit') || 'Gözden Geçir ve Gönder',
      description: t('reviewAllActions') || 'Tüm işlemleri gözden geçirin ve gönderin'
    }
  ];

  const visibleSteps = steps.filter(step => step.visible !== false);
  const currentStepData = visibleSteps[currentStep];

  const handleActionSelection = (actionType) => {
    setSelectedActions(prev => ({
      ...prev,
      [actionType]: !prev[actionType]
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Sync combined strings from split inputs
  useEffect(() => {
    const hasAny = stockQuantityInputs.stockWhole !== '' || stockQuantityInputs.stockDecimal !== '';
    const combined = hasAny
      ? `${stockQuantityInputs.stockWhole || '0'}.${(stockQuantityInputs.stockDecimal || '00').padEnd(2, '0').slice(0, 2)}`
      : '';
    setFormData(prev => ({ ...prev, ProductStockQuantity: combined }));
  }, [stockQuantityInputs.stockWhole, stockQuantityInputs.stockDecimal]);

  useEffect(() => {
    const hasAny = shippedQuantityInputs.shippedWhole !== '' || shippedQuantityInputs.shippedDecimal !== '';
    const combined = hasAny
      ? `${shippedQuantityInputs.shippedWhole || '0'}.${(shippedQuantityInputs.shippedDecimal || '00').padEnd(2, '0').slice(0, 2)}`
      : '';
    setFormData(prev => ({ ...prev, ProductShippedQuantity: combined }));
  }, [shippedQuantityInputs.shippedWhole, shippedQuantityInputs.shippedDecimal]);

  useEffect(() => {
    const hasAny = shipmentNetWeightInputs.netWhole !== '' || shipmentNetWeightInputs.netDecimal !== '';
    const combined = hasAny
      ? `${shipmentNetWeightInputs.netWhole || '0'}.${(shipmentNetWeightInputs.netDecimal || '00').padEnd(2, '0').slice(0, 2)}`
      : '';
    setFormData(prev => ({ ...prev, NetWeight: combined }));
  }, [shipmentNetWeightInputs.netWhole, shipmentNetWeightInputs.netDecimal]);

  useEffect(() => {
    const hasAny = shipmentGrossWeightInputs.grossWhole !== '' || shipmentGrossWeightInputs.grossDecimal !== '';
    const combined = hasAny
      ? `${shipmentGrossWeightInputs.grossWhole || '0'}.${(shipmentGrossWeightInputs.grossDecimal || '00').padEnd(2, '0').slice(0, 2)}`
      : '';
    setFormData(prev => ({ ...prev, GrossWeight: combined }));
  }, [shipmentGrossWeightInputs.grossWhole, shipmentGrossWeightInputs.grossDecimal]);

  // Handlers for decimal inputs (stock)
  const handleStockQtyChange = (part, value) => {
    if (part === 'whole' && !/^\d*$/.test(value)) return;
    if (part === 'decimal' && !/^\d{0,2}$/.test(value)) return;
    setStockQuantityInputs(prev => ({ ...prev, [part === 'whole' ? 'stockWhole' : 'stockDecimal']: value }));
    if (errors.ProductStockQuantity) setErrors(prev => ({ ...prev, ProductStockQuantity: '' }));
  };

  const handleStockQtyKeyDown = (e, part) => {
    if (part === 'whole') {
      const isDotOrCommaKey = e.key === '.' || e.key === ',' || e.keyCode === 190 || e.keyCode === 110 || e.keyCode === 188;
      if (isDotOrCommaKey) {
        e.preventDefault();
        const decimalInput = document.getElementById('orderActionStockQuantityDecimal');
        if (decimalInput) {
          decimalInput.focus();
          const valueLength = decimalInput.value ? decimalInput.value.length : 0;
          try { decimalInput.setSelectionRange(valueLength, valueLength); } catch (err) {}
        }
        return;
      }
    }
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    if (part === 'decimal') {
      const currentValue = e.target.value;
      const selectionStart = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;
      if (currentValue.length >= 2 && selectionStart === selectionEnd) {
        e.preventDefault();
        return;
      }
    }
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  const handleStockQtyFocus = (part) => {
    if (part === 'whole') {
      const input = document.getElementById('orderActionStockQuantityWhole');
      if (input && input.value) {
        setTimeout(() => input.select(), 0);
      }
    }
  };

  const handleStockQtyBlur = (part) => {
    if (part === 'decimal') {
      const currentValue = stockQuantityInputs.stockDecimal;
      if (currentValue !== '' && currentValue.length === 1) {
        setStockQuantityInputs(prev => ({ ...prev, stockDecimal: currentValue.padEnd(2, '0') }));
      } else if (currentValue === '' && stockQuantityInputs.stockWhole) {
        setStockQuantityInputs(prev => ({ ...prev, stockDecimal: '00' }));
      }
    }
  };

  // Handlers for decimal inputs (shipped)
  const handleShippedQtyChange = (part, value) => {
    if (part === 'whole' && !/^\d*$/.test(value)) return;
    if (part === 'decimal' && !/^\d{0,2}$/.test(value)) return;
    setShippedQuantityInputs(prev => ({ ...prev, [part === 'whole' ? 'shippedWhole' : 'shippedDecimal']: value }));
    if (errors.ProductShippedQuantity) setErrors(prev => ({ ...prev, ProductShippedQuantity: '' }));
  };

  const handleShippedQtyKeyDown = (e, part) => {
    if (part === 'whole') {
      const isDotOrCommaKey = e.key === '.' || e.key === ',' || e.keyCode === 190 || e.keyCode === 110 || e.keyCode === 188;
      if (isDotOrCommaKey) {
        e.preventDefault();
        const decimalInput = document.getElementById('orderActionShippedQuantityDecimal');
        if (decimalInput) {
          decimalInput.focus();
          const valueLength = decimalInput.value ? decimalInput.value.length : 0;
          try { decimalInput.setSelectionRange(valueLength, valueLength); } catch (err) {}
        }
        return;
      }
    }
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    if (part === 'decimal') {
      const currentValue = e.target.value;
      const selectionStart = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;
      if (currentValue.length >= 2 && selectionStart === selectionEnd) {
        e.preventDefault();
        return;
      }
    }
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  const handleShippedQtyFocus = (part) => {
    if (part === 'whole') {
      const input = document.getElementById('orderActionShippedQuantityWhole');
      if (input && input.value) {
        setTimeout(() => input.select(), 0);
      }
    }
  };

  const handleShippedQtyBlur = (part) => {
    if (part === 'decimal') {
      const currentValue = shippedQuantityInputs.shippedDecimal;
      if (currentValue !== '' && currentValue.length === 1) {
        setShippedQuantityInputs(prev => ({ ...prev, shippedDecimal: currentValue.padEnd(2, '0') }));
      } else if (currentValue === '' && shippedQuantityInputs.shippedWhole) {
        setShippedQuantityInputs(prev => ({ ...prev, shippedDecimal: '00' }));
      }
    }
  };

  // Handlers for decimal inputs (shipment net weight)
  const handleShipmentNetWeightChange = (part, value) => {
    if (part === 'whole' && !/^\d*$/.test(value)) return;
    if (part === 'decimal' && !/^\d{0,2}$/.test(value)) return;
    setShipmentNetWeightInputs(prev => ({ ...prev, [part === 'whole' ? 'netWhole' : 'netDecimal']: value }));
    if (errors.NetWeight) setErrors(prev => ({ ...prev, NetWeight: '' }));
  };

  const handleShipmentNetWeightKeyDown = (e, part) => {
    if (part === 'whole') {
      const isDotOrCommaKey = e.key === '.' || e.key === ',' || e.keyCode === 190 || e.keyCode === 110 || e.keyCode === 188;
      if (isDotOrCommaKey) {
        e.preventDefault();
        const decimalInput = document.getElementById('orderActionShipmentNetWeightDecimal');
        if (decimalInput) {
          decimalInput.focus();
          const valueLength = decimalInput.value ? decimalInput.value.length : 0;
          try { decimalInput.setSelectionRange(valueLength, valueLength); } catch (err) {}
        }
        return;
      }
    }
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    if (part === 'decimal') {
      const currentValue = e.target.value;
      const selectionStart = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;
      if (currentValue.length >= 2 && selectionStart === selectionEnd) {
        e.preventDefault();
        return;
      }
    }
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  const handleShipmentNetWeightFocus = (part) => {
    if (part === 'whole') {
      const input = document.getElementById('orderActionShipmentNetWeightWhole');
      if (input && input.value) {
        setTimeout(() => input.select(), 0);
      }
    }
  };

  const handleShipmentNetWeightBlur = (part) => {
    if (part === 'decimal') {
      const currentValue = shipmentNetWeightInputs.netDecimal;
      if (currentValue !== '' && currentValue.length === 1) {
        setShipmentNetWeightInputs(prev => ({ ...prev, netDecimal: currentValue.padEnd(2, '0') }));
      } else if (currentValue === '' && shipmentNetWeightInputs.netWhole) {
        setShipmentNetWeightInputs(prev => ({ ...prev, netDecimal: '00' }));
      }
    }
  };

  // Handlers for decimal inputs (shipment gross weight)
  const handleShipmentGrossWeightChange = (part, value) => {
    if (part === 'whole' && !/^\d*$/.test(value)) return;
    if (part === 'decimal' && !/^\d{0,2}$/.test(value)) return;
    setShipmentGrossWeightInputs(prev => ({ ...prev, [part === 'whole' ? 'grossWhole' : 'grossDecimal']: value }));
    if (errors.GrossWeight) setErrors(prev => ({ ...prev, GrossWeight: '' }));
  };

  const handleShipmentGrossWeightKeyDown = (e, part) => {
    if (part === 'whole') {
      const isDotOrCommaKey = e.key === '.' || e.key === ',' || e.keyCode === 190 || e.keyCode === 110 || e.keyCode === 188;
      if (isDotOrCommaKey) {
        e.preventDefault();
        const decimalInput = document.getElementById('orderActionShipmentGrossWeightDecimal');
        if (decimalInput) {
          decimalInput.focus();
          const valueLength = decimalInput.value ? decimalInput.value.length : 0;
          try { decimalInput.setSelectionRange(valueLength, valueLength); } catch (err) {}
        }
        return;
      }
    }
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    if (part === 'decimal') {
      const currentValue = e.target.value;
      const selectionStart = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;
      if (currentValue.length >= 2 && selectionStart === selectionEnd) {
        e.preventDefault();
        return;
      }
    }
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  const handleShipmentGrossWeightFocus = (part) => {
    if (part === 'whole') {
      const input = document.getElementById('orderActionShipmentGrossWeightWhole');
      if (input && input.value) {
        setTimeout(() => input.select(), 0);
      }
    }
  };

  const handleShipmentGrossWeightBlur = (part) => {
    if (part === 'decimal') {
      const currentValue = shipmentGrossWeightInputs.grossDecimal;
      if (currentValue !== '' && currentValue.length === 1) {
        setShipmentGrossWeightInputs(prev => ({ ...prev, grossDecimal: currentValue.padEnd(2, '0') }));
      } else if (currentValue === '' && shipmentGrossWeightInputs.grossWhole) {
        setShipmentGrossWeightInputs(prev => ({ ...prev, grossDecimal: '00' }));
      }
    }
  };

  const validateCurrentStep = () => {
    const newErrors = {};
    
    if (currentStepData.id === 'orderStatus') {
      if (!formData.NewOrderStatusGuid) {
        newErrors.NewOrderStatusGuid = t('orderStatusRequired') || 'Yeni sipariş durumu seçilmelidir';
      }
    }
    
    if (currentStepData.id === 'stock') {
      if (!formData.StockPrdUnitGuid) newErrors.StockPrdUnitGuid = t('stockUnitRequired') || 'Stok birimi seçilmelidir';
      if (!formData.OpsStockStatusGuid) newErrors.OpsStockStatusGuid = t('stockStatusRequired') || 'Stok durumu seçilmelidir';
      if (!formData.ProductStockQuantity) newErrors.ProductStockQuantity = t('stockQuantityRequired') || 'Stok miktarı girilmelidir';
      if (!formData.StockPlace) newErrors.StockPlace = t('stockPlaceRequired') || 'Stok yeri girilmelidir';
    }
    
    if (currentStepData.id === 'shipment') {
      if (!formData.OpsShipmentStatusGuid) newErrors.OpsShipmentStatusGuid = t('shipmentStatusRequired') || 'Sevkiyat durumu seçilmelidir';
      if (!formData.ShipmentPrdUnitGuid) newErrors.ShipmentPrdUnitGuid = t('shipmentUnitRequired') || 'Sevkiyat birimi seçilmelidir';
      if (!formData.BillingCompany) newErrors.BillingCompany = t('billingCompanyRequired') || 'Fatura şirketi girilmelidir';
      if (!formData.ShipmentDatetime) newErrors.ShipmentDatetime = t('shipmentDatetimeRequired') || 'Sevkiyat tarihi girilmelidir';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStepData.id === 'selection') {
      // Check if at least one action is selected
      const hasSelectedAction = Object.values(selectedActions).some(action => action);
      if (!hasSelectedAction) {
        setErrors({ selection: t('selectAtLeastOneAction') || 'En az bir işlem seçilmelidir' });
        return;
      }
      setErrors({});
      setCurrentStep(1);
    } else if (validateCurrentStep()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const isAllStepsCompleted = () => {
    // Check if selected action forms are filled
    if (selectedActions.orderStatus && !formData.NewOrderStatusGuid) {
      return false;
    }
    
    if (selectedActions.stock && (!formData.StockPrdUnitGuid || !formData.OpsStockStatusGuid || 
        !formData.ProductStockQuantity || !formData.StockPlace)) {
      return false;
    }
    
    if (selectedActions.shipment && (!formData.OpsShipmentStatusGuid || !formData.ShipmentPrdUnitGuid || 
        !formData.BillingCompany || !formData.ShipmentDatetime)) {
      return false;
    }
    
    return true;
  };

  const formatDateForBackend = (input) => {
    if (!input) return '';
    
    // input: '2025-07-22T11:53' veya '2025-07-22 11:58:00' gibi gelebilir
    // Sadece tarihi al, saati at
    let datePart = input.split('T')[0] || input.split(' ')[0];
    if (!datePart) datePart = input;
    
    const [year, month, day] = datePart.split('-');
    if (year && month && day) {
      // Sunucu DDMMYYYY formatını bekliyor olabilir
      const paddedDay = day.padStart(2, '0');
      const paddedMonth = month.padStart(2, '0');
      return `${paddedDay}${paddedMonth}${year}`;
    }
    
    // Eğer zaten doğru formatta ise dokunma
    if (datePart.includes(',') && datePart.split(',').length === 3) {
      // Virgülle ayrılmış formatı DDMMYYYY'ye çevir
      const [day, month, year] = datePart.split(',');
      const paddedDay = day.trim().padStart(2, '0');
      const paddedMonth = month.trim().padStart(2, '0');
      return `${paddedDay}${paddedMonth}${year.trim()}`;
    }
    
    return input;
  };

  const handleSubmit = () => {
    // Only submit if we're on the final step and all steps are completed
    if (currentStep !== visibleSteps.length - 1) {
      return;
    }
    
    if (!isAllStepsCompleted()) {
      return;
    }
    
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionUserId = localStorage.getItem('sessionUserId');
    const userId = sessionUserId || userData?.sessionUserId;

    if (!userId || userId === 'undefined') {
      setErrors({ general: 'User session not found. Please login again.' });
      return;
    }

    const submitData = {
      SessionUserId: String(userId),
      OrderGuid: String(id),
      NewOrderStatusGuid: selectedActions.orderStatus ? formData.NewOrderStatusGuid : null,
      StockAction: selectedActions.stock ? {
        StockPrdUnitGuid: formData.StockPrdUnitGuid,
        OpsStockStatusGuid: formData.OpsStockStatusGuid,
        ProductStockQuantity: toScaledIntegerStringFromParts(stockQuantityInputs.stockWhole, stockQuantityInputs.stockDecimal),
        StockRollCount: formData.StockRollCount,
        StockPlace: formData.StockPlace,
        Lot: formData.Lot,
        StockDescription: formData.StockDescription
      } : null,
      ShipmentAction: selectedActions.shipment ? {
        StockQuantity: toScaledIntegerStringFromParts(stockQuantityInputs.stockWhole, stockQuantityInputs.stockDecimal),
        OpsShipmentStatusGuid: formData.OpsShipmentStatusGuid,
        ShipmentPrdUnitGuid: formData.ShipmentPrdUnitGuid,
        BillingCompany: formData.BillingCompany,
        ShipmentDatetime: formatDateForBackend(formData.ShipmentDatetime),
        DeliveryNo: formData.DeliveryNo,
        ShipmentDescription: formData.ShipmentDescription,
        ProductShippedQuantity: toScaledIntegerStringFromParts(shippedQuantityInputs.shippedWhole, shippedQuantityInputs.shippedDecimal),
        NetWeight: (shipmentNetWeightInputs.netWhole !== '' || shipmentNetWeightInputs.netDecimal !== '')
          ? toScaledIntegerStringFromParts(shipmentNetWeightInputs.netWhole, shipmentNetWeightInputs.netDecimal)
          : '',
        GrossWeight: (shipmentGrossWeightInputs.grossWhole !== '' || shipmentGrossWeightInputs.grossDecimal !== '')
          ? toScaledIntegerStringFromParts(shipmentGrossWeightInputs.grossWhole, shipmentGrossWeightInputs.grossDecimal)
          : '',
        Lot: formData.ShipmentLot,
        ShipmentRollCount: formData.ShipmentRollCount
      } : null,
      navigate: navigate
    };

    orderActionRequest(submitData);
  };

  const handleCancel = () => {
    navigate('/orders');
  };

  const handleGoBack = () => {
    navigate('/orders');
  };

  const renderStepContent = () => {
    switch (currentStepData.id) {
      case 'selection':
        return (
          <div className="action-selection">
            {errors.selection && (
              <CAlert color="danger" className="mb-3">
                <CIcon icon={cilXCircle} className="me-2" />
                {errors.selection}
              </CAlert>
            )}
            <div className="selection-grid">
              <div 
                className={`selection-card ${selectedActions.orderStatus ? 'selected' : ''}`}
                onClick={() => handleActionSelection('orderStatus')}
              >
                <div className="card-icon">
                  <CIcon icon={cilInfo} size="xl" />
                </div>
                <h5>{t('orderStatusChange') || 'Durum Değişikliği'}</h5>
                <p>{t('changeOrderStatus') || 'Sipariş durumunu değiştirin'}</p>
              </div>
              
              <div 
                className={`selection-card ${selectedActions.stock ? 'selected' : ''}`}
                onClick={() => handleActionSelection('stock')}
              >
                <div className="card-icon">
                  <CIcon icon={cilFolderOpen} size="xl" />
                </div>
                <h5>{t('stockAction') || 'Stok İşlemi'}</h5>
                <p>{t('manageStock') || 'Stok işlemlerini yönetin'}</p>
              </div>
              
              <div 
                className={`selection-card ${selectedActions.shipment ? 'selected' : ''}`}
                onClick={() => handleActionSelection('shipment')}
              >
                <div className="card-icon">
                  <CIcon icon={cilTruck} size="xl" />
                </div>
                <h5>{t('shipmentAction') || 'Sevkiyat İşlemi'}</h5>
                <p>{t('manageShipment') || 'Sevkiyat işlemlerini yönetin'}</p>
              </div>
            </div>
          </div>
        );

      case 'orderStatus':
        return (
          <div className="form-step">
            <CRow>
              <CCol md={6}>
                <div className="form-group">
                  <CFormLabel htmlFor="orderStatusGuid">
                    {t('orderStatus') || 'Yeni Sipariş Durumu'} <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormSelect
                    id="NewOrderStatusGuid"
                    name="NewOrderStatusGuid"
                    value={formData.NewOrderStatusGuid}
                    onChange={handleInputChange}
                    className={errors.NewOrderStatusGuid ? 'is-invalid' : ''}
                    disabled={dropdownLoading}
                  >
                    <option value="">{dropdownLoading ? t('loading') || 'Yükleniyor...' : t('selectStatus') || 'Durum Seçin'}</option>
                    {orderStatusOptions.map((item) => (
                      <option key={item.OpsOrderStatusGuid} value={item.OpsOrderStatusGuid}>
                        {item.Name || item.OpsOrderStatusGuid}
                      </option>
                    ))}
                  </CFormSelect>
                  {errors.NewOrderStatusGuid && (
                    <div className="invalid-feedback d-block">{errors.NewOrderStatusGuid}</div>
                  )}
                </div>
              </CCol>
            </CRow>
          </div>
        );

      case 'stock':
        return (
          <div className="form-step">
            <CRow>
              <CCol md={6}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="StockPrdUnitGuid">
                    {t('stockUnit') || 'Stok Birimi'} <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormSelect
                    id="StockPrdUnitGuid"
                    name="StockPrdUnitGuid"
                    value={formData.StockPrdUnitGuid}
                    onChange={handleInputChange}
                    className={errors.StockPrdUnitGuid ? 'is-invalid' : ''}
                    disabled={dropdownLoading}
                  >
                    <option value="">{dropdownLoading ? t('loading') || 'Yükleniyor...' : t('selectUnit') || 'Birim Seçin'}</option>
                    {unitOptions.map((item) => (
                      <option key={item.PrdUnitGuid} value={item.PrdUnitGuid}>
                        {item.UnitName || item.PrdUnitGuid}
                      </option>
                    ))}
                  </CFormSelect>
                  {errors.StockPrdUnitGuid && (
                    <div className="invalid-feedback d-block">{errors.StockPrdUnitGuid}</div>
                  )}
                </div>
              </CCol>
              <CCol md={6}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="OpsStockStatusGuid">
                    {t('stockStatus') || 'Stok Durumu'} <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormSelect
                    id="OpsStockStatusGuid"
                    name="OpsStockStatusGuid"
                    value={formData.OpsStockStatusGuid}
                    onChange={handleInputChange}
                    className={errors.OpsStockStatusGuid ? 'is-invalid' : ''}
                    disabled={dropdownLoading}
                  >
                    <option value="">{dropdownLoading ? t('loading') || 'Yükleniyor...' : t('selectStockStatus') || 'Stok Durumu Seçin'}</option>
                    {stockStatusOptions.map((item) => (
                      <option key={item.OpsStockStatusGuid} value={item.OpsStockStatusGuid}>
                        {item.Name || item.OpsStockStatusGuid}
                      </option>
                    ))}
                  </CFormSelect>
                  {errors.OpsStockStatusGuid && (
                    <div className="invalid-feedback d-block">{errors.OpsStockStatusGuid}</div>
                  )}
                </div>
              </CCol>
            </CRow>
            <CRow>
              <CCol md={6}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="ProductStockQuantity">
                    {t('stockQuantity') || 'Stok Miktarı'} <span className="text-danger">*</span>
                  </CFormLabel>
                  <div className={`price-input-group ${errors.ProductStockQuantity ? 'is-invalid' : ''}`}>
                    <div className="price-input-section">
                      <CFormInput
                        type="text"
                        id="orderActionStockQuantityWhole"
                        className="price-whole"
                        value={stockQuantityInputs.stockWhole}
                        onChange={(e) => handleStockQtyChange('whole', e.target.value)}
                        onKeyDown={(e) => handleStockQtyKeyDown(e, 'whole')}
                        onFocus={() => handleStockQtyFocus('whole')}
                        placeholder="0"
                      />
                      <span className="decimal-separator">,</span>
                      <CFormInput
                        type="text"
                        id="orderActionStockQuantityDecimal"
                        className="price-decimal"
                        value={stockQuantityInputs.stockDecimal}
                        onChange={(e) => handleStockQtyChange('decimal', e.target.value)}
                        onKeyDown={(e) => handleStockQtyKeyDown(e, 'decimal')}
                        onFocus={() => handleStockQtyFocus('decimal')}
                        onBlur={() => handleStockQtyBlur('decimal')}
                        placeholder="00"
                        maxLength="2"
                      />
                    </div>
                  </div>
                  {errors.ProductStockQuantity && (
                    <div className="invalid-feedback d-block">{errors.ProductStockQuantity}</div>
                  )}
                </div>
              </CCol>
              <CCol md={6}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="StockPlace">
                    {t('stockPlace') || 'Stok Yeri'} <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormInput
                    type="text"
                    id="StockPlace"
                    name="StockPlace"
                    value={formData.StockPlace}
                    onChange={handleInputChange}
                    className={errors.StockPlace ? 'is-invalid' : ''}
                    placeholder={t('enterStockPlace') || 'Stok yeri girin'}
                  />
                  {errors.StockPlace && (
                    <div className="invalid-feedback d-block">{errors.StockPlace}</div>
                  )}
                </div>
              </CCol>
            </CRow>
            <CRow>
              <CCol md={6}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="StockRollCount">
                    {t('stockRollCount') || 'Top Sayısı'}
                  </CFormLabel>
                  <CFormInput
                    type="number"
                    min="0"
                    step="1"
                    id="StockRollCount"
                    name="StockRollCount"
                    value={formData.StockRollCount}
                    onChange={handleInputChange}
                    placeholder={t('enterStockRollCount')}
                  />
                </div>
              </CCol>
              <CCol md={6}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="Lot">
                    {t('lot') || 'Lot'}
                  </CFormLabel>
                  <CFormInput
                    type="text"
                    id="Lot"
                    name="Lot"
                    value={formData.Lot}
                    onChange={handleInputChange}
                    className={errors.Lot ? 'is-invalid' : ''}
                    placeholder={t('enterLot') || 'Lot bilgisi girin'}
                  />
                  {errors.Lot && (
                    <div className="invalid-feedback d-block">{errors.Lot}</div>
                  )}
                </div>
              </CCol>
            </CRow>
            <CRow>
              <CCol md={12}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="StockDescription">
                    {t('stockDescription') || 'Stok Açıklaması'}
                  </CFormLabel>
                  <CFormInput
                    type="text"
                    id="StockDescription"
                    name="StockDescription"
                    value={formData.StockDescription}
                    onChange={handleInputChange}
                    placeholder={t('enterStockDescription') || 'Stok açıklaması girin'}
                  />
                </div>
              </CCol>
            </CRow>
          </div>
        );

      case 'shipment':
        return (
          <div className="form-step">
            <CRow>
              <CCol md={6}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="OpsShipmentStatusGuid">
                    {t('shipmentStatus') || 'Sevkiyat Durumu'} <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormSelect
                    id="OpsShipmentStatusGuid"
                    name="OpsShipmentStatusGuid"
                    value={formData.OpsShipmentStatusGuid}
                    onChange={handleInputChange}
                    className={errors.OpsShipmentStatusGuid ? 'is-invalid' : ''}
                    disabled={dropdownLoading}
                  >
                    <option value="">{dropdownLoading ? t('loading') || 'Yükleniyor...' : t('selectShipmentStatus') || 'Sevkiyat Durumu Seçin'}</option>
                    {shipmentStatusOptions.map((item) => (
                      <option key={item.OpsShipmentStatusGuid} value={item.OpsShipmentStatusGuid}>
                        {item.Name || item.OpsShipmentStatusGuid}
                      </option>
                    ))}
                  </CFormSelect>
                  {errors.OpsShipmentStatusGuid && (
                    <div className="invalid-feedback d-block">{errors.OpsShipmentStatusGuid}</div>
                  )}
                </div>
              </CCol>
              <CCol md={6}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="ShipmentPrdUnitGuid">
                    {t('shipmentUnit') || 'Sevkiyat Birimi'} <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormSelect
                    id="ShipmentPrdUnitGuid"
                    name="ShipmentPrdUnitGuid"
                    value={formData.ShipmentPrdUnitGuid}
                    onChange={handleInputChange}
                    className={errors.ShipmentPrdUnitGuid ? 'is-invalid' : ''}
                    disabled={dropdownLoading}
                  >
                    <option value="">{dropdownLoading ? t('loading') || 'Yükleniyor...' : t('selectShipmentUnit') || 'Sevkiyat Birimi Seçin'}</option>
                    {unitOptions.map((item) => (
                      <option key={item.PrdUnitGuid} value={item.PrdUnitGuid}>
                        {item.UnitName || item.PrdUnitGuid}
                      </option>
                    ))}
                  </CFormSelect>
                  {errors.ShipmentPrdUnitGuid && (
                    <div className="invalid-feedback d-block">{errors.ShipmentPrdUnitGuid}</div>
                  )}
                </div>
              </CCol>
            </CRow>
            <CRow>
              <CCol md={6}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="BillingCompany">
                    {t('billingCompany') || 'Fatura Şirketi'} <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormInput
                    type="text"
                    id="BillingCompany"
                    name="BillingCompany"
                    value={formData.BillingCompany}
                    onChange={handleInputChange}
                    className={errors.BillingCompany ? 'is-invalid' : ''}
                    placeholder={t('enterBillingCompany') || 'Fatura şirketi girin'}
                  />
                  {errors.BillingCompany && (
                    <div className="invalid-feedback d-block">{errors.BillingCompany}</div>
                  )}
                </div>
              </CCol>
              <CCol md={6}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="ShipmentDatetime">
                    {t('shipmentDatetime') || 'Sevkiyat Tarihi'} <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormInput
                    type="date"
                    id="ShipmentDatetime"
                    name="ShipmentDatetime"
                    value={formData.ShipmentDatetime}
                    onChange={handleInputChange}
                    className={errors.ShipmentDatetime ? 'is-invalid' : ''}
                  />
                  {errors.ShipmentDatetime && (
                    <div className="invalid-feedback d-block">{errors.ShipmentDatetime}</div>
                  )}
                </div>
              </CCol>
            </CRow>
            <CRow>
              <CCol md={6}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="DeliveryNo">
                    {t('deliveryNo') || 'Teslimat Numarası'}
                  </CFormLabel>
                  <CFormInput
                    type="text"
                    id="DeliveryNo"
                    name="DeliveryNo"
                    value={formData.DeliveryNo}
                    onChange={handleInputChange}
                    className={errors.DeliveryNo ? 'is-invalid' : ''}
                    placeholder={t('enterDeliveryNo') || 'Teslimat numarası girin'}
                  />
                  {errors.DeliveryNo && (
                    <div className="invalid-feedback d-block">{errors.DeliveryNo}</div>
                  )}
                </div>
              </CCol>
              <CCol md={6}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="ProductShippedQuantity">
                    {t('productShippedQuantity') || 'Sevk Edilen Ürün Miktarı'}
                  </CFormLabel>
                  <div className="price-input-group">
                    <div className="price-input-section">
                      <CFormInput
                        type="text"
                        id="orderActionShippedQuantityWhole"
                        className="price-whole"
                        value={shippedQuantityInputs.shippedWhole}
                        onChange={(e) => handleShippedQtyChange('whole', e.target.value)}
                        onKeyDown={(e) => handleShippedQtyKeyDown(e, 'whole')}
                        onFocus={() => handleShippedQtyFocus('whole')}
                        placeholder="0"
                      />
                      <span className="decimal-separator">,</span>
                      <CFormInput
                        type="text"
                        id="orderActionShippedQuantityDecimal"
                        className="price-decimal"
                        value={shippedQuantityInputs.shippedDecimal}
                        onChange={(e) => handleShippedQtyChange('decimal', e.target.value)}
                        onKeyDown={(e) => handleShippedQtyKeyDown(e, 'decimal')}
                        onFocus={() => handleShippedQtyFocus('decimal')}
                        onBlur={() => handleShippedQtyBlur('decimal')}
                        placeholder="00"
                        maxLength="2"
                      />
                    </div>
                  </div>
                </div>
              </CCol>
            </CRow>
            <CRow>
              <CCol md={4}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="orderActionShipmentNetWeightWhole">
                    {t('netWeight') || 'Net Weight'}
                  </CFormLabel>
                  <div className="price-input-group">
                    <div className="price-input-section">
                      <CFormInput
                        type="text"
                        id="orderActionShipmentNetWeightWhole"
                        className="price-whole"
                        value={shipmentNetWeightInputs.netWhole}
                        onChange={(e) => handleShipmentNetWeightChange('whole', e.target.value)}
                        onKeyDown={(e) => handleShipmentNetWeightKeyDown(e, 'whole')}
                        onFocus={() => handleShipmentNetWeightFocus('whole')}
                        placeholder="0"
                      />
                      <span className="decimal-separator">,</span>
                      <CFormInput
                        type="text"
                        id="orderActionShipmentNetWeightDecimal"
                        className="price-decimal"
                        value={shipmentNetWeightInputs.netDecimal}
                        onChange={(e) => handleShipmentNetWeightChange('decimal', e.target.value)}
                        onKeyDown={(e) => handleShipmentNetWeightKeyDown(e, 'decimal')}
                        onFocus={() => handleShipmentNetWeightFocus('decimal')}
                        onBlur={() => handleShipmentNetWeightBlur('decimal')}
                        placeholder="00"
                        maxLength="2"
                      />
                    </div>
                  </div>
                </div>
              </CCol>
              <CCol md={4}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="orderActionShipmentGrossWeightWhole">
                    {t('grossWeight') || 'Gross Weight'}
                  </CFormLabel>
                  <div className="price-input-group">
                    <div className="price-input-section">
                      <CFormInput
                        type="text"
                        id="orderActionShipmentGrossWeightWhole"
                        className="price-whole"
                        value={shipmentGrossWeightInputs.grossWhole}
                        onChange={(e) => handleShipmentGrossWeightChange('whole', e.target.value)}
                        onKeyDown={(e) => handleShipmentGrossWeightKeyDown(e, 'whole')}
                        onFocus={() => handleShipmentGrossWeightFocus('whole')}
                        placeholder="0"
                      />
                      <span className="decimal-separator">,</span>
                      <CFormInput
                        type="text"
                        id="orderActionShipmentGrossWeightDecimal"
                        className="price-decimal"
                        value={shipmentGrossWeightInputs.grossDecimal}
                        onChange={(e) => handleShipmentGrossWeightChange('decimal', e.target.value)}
                        onKeyDown={(e) => handleShipmentGrossWeightKeyDown(e, 'decimal')}
                        onFocus={() => handleShipmentGrossWeightFocus('decimal')}
                        onBlur={() => handleShipmentGrossWeightBlur('decimal')}
                        placeholder="00"
                        maxLength="2"
                      />
                    </div>
                  </div>
                </div>
              </CCol>
              <CCol md={4}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="ShipmentLot">
                    {t('lot') || 'Lot'}
                  </CFormLabel>
                  <CFormInput
                    type="text"
                    id="ShipmentLot"
                    name="ShipmentLot"
                    value={formData.ShipmentLot}
                    onChange={handleInputChange}
                    placeholder={t('enterLot') || 'Lot bilgisi girin'}
                  />
                </div>
              </CCol>
            </CRow>
            <CRow>
              <CCol md={12}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="ShipmentRollCount">
                    {t('shipmentRollCount') || 'Top Sayısı'}
                  </CFormLabel>
                  <CFormInput
                    type="number"
                    min="0"
                    step="1"
                    id="ShipmentRollCount"
                    name="ShipmentRollCount"
                    value={formData.ShipmentRollCount}
                    onChange={handleInputChange}
                    placeholder={t('enterShipmentRollCount')}
                  />
                </div>
              </CCol>
            </CRow>
            <CRow>
              <CCol md={12}>
                <div className="form-group mb-3">
                  <CFormLabel htmlFor="ShipmentDescription">
                    {t('shipmentDescription') || 'Sevkiyat Açıklaması'}
                  </CFormLabel>
                  <CFormInput
                    type="text"
                    id="ShipmentDescription"
                    name="ShipmentDescription"
                    value={formData.ShipmentDescription}
                    onChange={handleInputChange}
                    placeholder={t('enterShipmentDescription') || 'Sevkiyat açıklaması girin'}
                  />
                </div>
              </CCol>
            </CRow>
          </div>
        );

      case 'review':
        return (
          <div className="review-step">
            <div className="review-summary">
              <h5>{t('selectedActions') || 'Seçilen İşlemler'}</h5>
              
              {selectedActions.orderStatus && (
                <div className="review-item">
                  <h6>{t('orderStatusChange') || 'Durum Değişikliği'}</h6>
                  <p><strong>{t('orderStatus') || 'Yeni Durum'}:</strong> {
                    orderStatusOptions.find(item => item.OpsOrderStatusGuid === formData.NewOrderStatusGuid)?.Name || 
                    formData.NewOrderStatusGuid || 
                    t('notSet') || 'Ayarlanmamış'
                  }</p>
                </div>
              )}
              
              {selectedActions.stock && (
                <div className="review-item">
                  <h6>{t('stockAction') || 'Stok İşlemi'}</h6>
                  <p><strong>{t('stockUnit') || 'Stok Birimi'}:</strong> {
                    unitOptions.find(item => item.PrdUnitGuid === formData.StockPrdUnitGuid)?.UnitName || 
                    formData.StockPrdUnitGuid || 
                    t('notSet') || 'Ayarlanmamış'
                  }</p>
                  <p><strong>{t('stockStatus') || 'Stok Durumu'}:</strong> {
                    stockStatusOptions.find(item => item.OpsStockStatusGuid === formData.OpsStockStatusGuid)?.Name || 
                    formData.OpsStockStatusGuid || 
                    t('notSet') || 'Ayarlanmamış'
                  }</p>
                  <p><strong>{t('stockQuantity') || 'Stok Miktarı'}:</strong> {
                    (stockQuantityInputs.stockWhole !== '' || stockQuantityInputs.stockDecimal !== '')
                      ? formatScaledAmount(toScaledIntegerStringFromParts(stockQuantityInputs.stockWhole, stockQuantityInputs.stockDecimal))
                      : (t('notSet') || 'Ayarlanmamış')
                  }</p>
                  <p><strong>{t('stockRollCount') || 'Top Sayısı'}:</strong> {formData.StockRollCount || t('notSet') || 'Ayarlanmamış'}</p>
                  <p><strong>{t('stockPlace') || 'Stok Yeri'}:</strong> {formData.StockPlace || t('notSet') || 'Ayarlanmamış'}</p>
                  <p><strong>{t('lot') || 'Lot'}:</strong> {formData.Lot || t('notSet') || 'Ayarlanmamış'}</p>
                  {formData.StockDescription && <p><strong>{t('stockDescription') || 'Açıklama'}:</strong> {formData.StockDescription}</p>}
                </div>
              )}
              
              {selectedActions.shipment && (
                <div className="review-item">
                  <h6>{t('shipmentAction') || 'Sevkiyat İşlemi'}</h6>
                  <p><strong>{t('shipmentStatus') || 'Sevkiyat Durumu'}:</strong> {
                    shipmentStatusOptions.find(item => item.OpsShipmentStatusGuid === formData.OpsShipmentStatusGuid)?.Name || 
                    formData.OpsShipmentStatusGuid || 
                    t('notSet') || 'Ayarlanmamış'
                  }</p>
                  <p><strong>{t('shipmentUnit') || 'Sevkiyat Birimi'}:</strong> {
                    unitOptions.find(item => item.PrdUnitGuid === formData.ShipmentPrdUnitGuid)?.UnitName || 
                    formData.ShipmentPrdUnitGuid || 
                    t('notSet') || 'Ayarlanmamış'
                  }</p>
                  <p><strong>{t('billingCompany') || 'Fatura Şirketi'}:</strong> {formData.BillingCompany || t('notSet') || 'Ayarlanmamış'}</p>
                  <p><strong>{t('shipmentDatetime') || 'Sevkiyat Tarihi'}:</strong> {formData.ShipmentDatetime || t('notSet') || 'Ayarlanmamış'}</p>
                  <p><strong>{t('deliveryNo') || 'Teslimat Numarası'}:</strong> {formData.DeliveryNo || t('notSet') || 'Ayarlanmamış'}</p>
                  {(shippedQuantityInputs.shippedWhole !== '' || shippedQuantityInputs.shippedDecimal !== '') && (
                    <p><strong>{t('productShippedQuantity') || 'Sevk Edilen Miktar'}:</strong> {formatScaledAmount(toScaledIntegerStringFromParts(shippedQuantityInputs.shippedWhole, shippedQuantityInputs.shippedDecimal))}</p>
                  )}
                  {(shipmentNetWeightInputs.netWhole !== '' || shipmentNetWeightInputs.netDecimal !== '') && (
                    <p><strong>{t('netWeight') || 'Net Weight'}:</strong> {formatScaledAmount(toScaledIntegerStringFromParts(shipmentNetWeightInputs.netWhole, shipmentNetWeightInputs.netDecimal))}</p>
                  )}
                  {(shipmentGrossWeightInputs.grossWhole !== '' || shipmentGrossWeightInputs.grossDecimal !== '') && (
                    <p><strong>{t('grossWeight') || 'Gross Weight'}:</strong> {formatScaledAmount(toScaledIntegerStringFromParts(shipmentGrossWeightInputs.grossWhole, shipmentGrossWeightInputs.grossDecimal))}</p>
                  )}
                  <p><strong>{t('lot') || 'Lot'}:</strong> {formData.ShipmentLot || t('notSet') || 'Ayarlanmamış'}</p>
                  <p><strong>{t('shipmentRollCount') || 'Top Sayısı'}:</strong> {formData.ShipmentRollCount || t('notSet') || 'Ayarlanmamış'}</p>
                  {formData.ShipmentDescription && <p><strong>{t('shipmentDescription') || 'Açıklama'}:</strong> {formData.ShipmentDescription}</p>}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!orderInfo) {
    return (
      <div className="order-action-not-found">
        <CButton
          color="secondary"
          variant="outline"
          onClick={handleGoBack}
          className="mb-3"
        >
          <CIcon icon={cilArrowLeft} className="me-2" />
          {t('goBack') || 'Geri Dön'}
        </CButton>
        <CAlert color="warning" className="warning-alert">
          <CIcon icon={cilWarning} className="me-2" />
          {t('orderNotFound') || 'Sipariş bilgisi bulunamadı.'}
        </CAlert>
      </div>
    );
  }

  return (
    <div className="order-action-container order-inputs-container">
      {/* Header */}
      <div className="order-action-header">
        <div className="header-left">
          <CButton
            color="secondary"
            variant="outline"
            onClick={handleGoBack}
            className="back-button"
          >
            <CIcon icon={cilArrowLeft} className="me-2" />
            {t('goBack') || 'Geri Dön'}
          </CButton>
          <div className="header-title">
            <h3>{t('orderAction') || 'Sipariş İşlemi'}</h3>
            <span className="header-subtitle">
              {orderInfo.OrderNo || orderInfo.WeaveCode || orderInfo.ProductCodeForCustomer || orderInfo.OrderGuid}
            </span>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="wizard-progress mb-4">
        <CProgress 
          value={(currentStep / (visibleSteps.length - 1)) * 100} 
          className="progress-bar"
        />
        <div className="progress-text">
          {t('step') || 'Adım'} {currentStep + 1} / {visibleSteps.length}
        </div>
      </div>

      {/* Order Info Card */}
      <CCard className="order-info-card mb-4">
        <CCardBody>
          <CRow>
            <CCol md={3}>
              <div className="info-item">
                <label className="info-label">{t('orderNo') || 'Sipariş No'}</label>
                <div className="info-value">{orderInfo.OrderNo || orderInfo.OrderGuid}</div>
              </div>
            </CCol>
            <CCol md={3}>
              <div className="info-item">
                <label className="info-label">{t('productCode') || 'Ürün Kodu'}</label>
                <div className="info-value">{orderInfo.WeaveCode || orderInfo.ProductCodeForCustomer || '-'}</div>
              </div>
            </CCol>
            <CCol md={3}>
              <div className="info-item">
                <label className="info-label">{t('customer') || 'Müşteri'}</label>
                <div className="info-value">{orderInfo.CustomerName}</div>
              </div>
            </CCol>
            <CCol md={3}>
              <div className="info-item">
                <label className="info-label">{t('currentStatus') || 'Mevcut Durum'}</label>
                <div className="info-value">{orderInfo.Name}</div>
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {/* Error Alert */}
      {errors.general && (
        <CAlert color="danger" className="mb-3">
          <CIcon icon={cilXCircle} className="me-2" />
          {errors.general}
        </CAlert>
      )}

      {/* Dropdown Error Alert */}
      {dropdownError && (
        <CAlert color="danger" className="mb-3">
          <CIcon icon={cilXCircle} className="me-2" />
          {dropdownError}
        </CAlert>
      )}



      {/* Wizard Form */}
      <CCard className="wizard-form-card">
        <CCardHeader className="form-header">
          <h5>{currentStepData.title}</h5>
          <p className="step-description">{currentStepData.description}</p>
        </CCardHeader>
        <CCardBody>
          <div>
            {renderStepContent()}

            {/* Completion Status */}
            {currentStep === visibleSteps.length - 1 && (
              <div className="completion-status mb-3">
                {isAllStepsCompleted() ? (
                  <CAlert color="success" className="mb-0">
                    <CIcon icon={cilCheckCircle} className="me-2" />
                    {t('allStepsCompleted') || 'Tüm adımlar tamamlandı. İşlemi gerçekleştirebilirsiniz.'}
                  </CAlert>
                ) : (
                  <CAlert color="warning" className="mb-0">
                    <CIcon icon={cilWarning} className="me-2" />
                    {t('stepsIncomplete') || 'Tüm adımlar tamamlanmadı. Lütfen eksik bilgileri doldurun.'}
                  </CAlert>
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="wizard-navigation">
              {currentStep > 0 && (
                <CButton
                  color="secondary"
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={isLoading}
                  className="nav-button"
                >
                  <CIcon icon={cilArrowLeftIcon} className="me-2" />
                  {t('previous') || 'Önceki'}
                </CButton>
              )}
              
              <div className="nav-right">
                {currentStep < visibleSteps.length - 1 ? (
                  <>
                    <CButton
                      color="primary"
                      onClick={handleNext}
                      disabled={isLoading}
                      className="nav-button"
                    >
                      {t('next') || 'İleri'}
                      <CIcon icon={cilArrowRight} className="ms-2" />
                    </CButton>
                    
                    <CButton
                      color="secondary"
                      variant="outline"
                      onClick={handleCancel}
                      disabled={isLoading}
                      className="cancel-button"
                    >
                      <CIcon icon={cilX} className="me-2" />
                      {t('cancel') || 'İptal'}
                    </CButton>
                  </>
                ) : (
                  <>
                    <CButton
                      color="success"
                      onClick={handleSubmit}
                      disabled={isLoading || !isAllStepsCompleted()}
                      className="submit-button"
                    >
                      {isLoading ? (
                        <>
                          <CSpinner size="sm" className="me-2" />
                          {t('processing') || 'İşleniyor...'}
                        </>
                      ) : (
                        <>
                          <CIcon icon={cilSave} className="me-2" />
                          {t('executeAction') || 'İşlemi Gerçekleştir'}
                        </>
                      )}
                    </CButton>
                    
                    <CButton
                      color="secondary"
                      variant="outline"
                      onClick={handleCancel}
                      disabled={isLoading}
                      className="cancel-button"
                    >
                      <CIcon icon={cilX} className="me-2" />
                      {t('cancel') || 'İptal'}
                    </CButton>
                  </>
                )}
              </div>
            </div>
          </div>
        </CCardBody>
      </CCard>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    isLoading: state.orderReducer?.isLoading || false,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    orderActionRequest: (payload) => {
      dispatch(orderActionRequest(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderAction); 
